import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Gem, Package } from 'lucide-react'
import SkinCarousel from '../components/SkinCarousel'
import TasksList from '../components/TasksList'
import TermsAndInfo from '../components/TermsAndInfo'
import useAudioManager from '../hooks/useAudioManager'
import { EventBanner } from '../features/event'
import useAppStore from '../store/useAppStore'

const Home = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { balance, telegramUser, user } = useAppStore()
  const { playSound } = useAudioManager({
    pick: 'pick'
  })

  const isEventActive = true

  const handlePaymentCardClick = () => {
    playSound('pick')
    navigate('/payment')
  }

  const balanceCard = (telegramUser || user) && (
    <div
      onClick={handlePaymentCardClick}
      className="cursor-pointer rounded-lg border border-white border-opacity-10 bg-gradient-to-r from-white/5 via-surface to-accent-primary/10 p-4 transition-all active:scale-[0.98] hover:border-accent-primary/30 lg:p-5"
    >
      <div className="flex items-center justify-between gap-4 lg:flex-col lg:items-start">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-accent-primary to-accent-secondary lg:h-12 lg:w-12">
            <Gem size={20} className="text-dark-primary lg:h-6 lg:w-6" />
          </div>
          <div>
            <p className="text-sm text-text-secondary">{t('home.balance')}</p>
            <p className="text-xl font-bold text-accent-primary lg:text-2xl">
              {balance.toLocaleString()}
            </p>
          </div>
        </div>
        <button className="rounded-lg bg-gradient-to-r from-accent-primary to-accent-secondary px-4 py-2 font-semibold text-dark-primary transition-opacity hover:opacity-90 lg:w-full">
          {t('home.topUp')}
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-dark-primary text-white">
      <SkinCarousel />

      <div className="space-y-6 px-6 py-6 lg:space-y-8 lg:px-10 lg:py-0 lg:pb-8 lg:px-0">
        {!isEventActive && (
          <div className="py-8 text-center lg:py-12 lg:hidden">
            <h1 data-text="SKINOZ" className="glitch mb-4 bg-gradient-to-r from-accent-primary to-accent-secondary bg-clip-text text-6xl font-orbitron font-black text-transparent lg:text-7xl">
              {t('home.title')}
            </h1>
            <p className="whitespace-pre-line bg-gradient-to-r from-white/90 to-accent-secondary bg-clip-text text-lg leading-relaxed text-transparent lg:mx-auto lg:max-w-3xl lg:text-xl">
              {t('home.description')}
            </p>
          </div>
        )}

        {isEventActive && (
          <div className="lg:hidden">
            <EventBanner />
          </div>
        )}

        <div className="lg:hidden">
          {balanceCard}
        </div>

        <div className="space-y-6 lg:grid lg:grid-cols-12 lg:items-start lg:gap-6 lg:space-y-0">
          <div className="space-y-6 lg:col-span-8">
            <TasksList />
          </div>

          <div className="space-y-6 lg:col-span-4 lg:sticky lg:top-24">
            {isEventActive && (
              <div className="hidden lg:block">
                <EventBanner />
              </div>
            )}

            <div className="pb-3 lg:pb-0">
              <button
                onClick={() => navigate('/cases')}
                className="flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary p-5 text-xl font-bold text-dark-primary shadow-2xl shadow-accent-primary/30 transition-all active:scale-[0.98] hover:scale-[1.02] hover:opacity-90 lg:min-h-[124px] lg:flex-col lg:items-start"
              >
                <Package size={28} />
                <div className="text-left">
                  <div>{t('home.openCases')}</div>
                  <div className="text-sm font-normal opacity-90">{t('home.openCasesDescription')}</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <TermsAndInfo />
    </div>
  )
}

export default Home
